<template>
    <report
        :report="report"
        :versions="versions"
        :loading="loading"
        :refreshing="refreshing"
        @reload="reload"
        @load-version="load_version"
    >
        <div class="lam-graph-container">
            <div class="uk-grid uk-grid-small uk-grid-width-1-3">
                <div>
                    <ul class="uk-subnav uk-subnav-line">
                        <li :class="{'uk-active': !state.filter}"><a href @click.prevent="set_state_value('filter', '')">All</a></li>
                        <li v-if="has_filtered_projects('pic')" :class="{'uk-active': state.filter === 'pic'}"><a href @click.prevent="set_state_value('filter', 'pic')">PIC</a></li>
                        <li v-if="has_filtered_projects('pm')" :class="{'uk-active': state.filter === 'pm'}"><a href @click.prevent="set_state_value('filter', 'pm')">PM</a></li>
                        <li v-if="has_filtered_projects('d')" :class="{'uk-active': state.filter === 'd'}"><a href @click.prevent="set_state_value('filter', 'd')">D</a></li>
                        <li v-if="has_filtered_projects('starred')" :class="{'uk-active': state.filter === 'starred'}"><a href @click.prevent="set_state_value('filter', 'starred')"><open-icon :glyph="(state.filter === 'starred') ? 'star' : 'star-o'" /></a></li>
                    </ul>
                </div>
                <form @submit.prevent.stop class="uk-form uk-text-center">
                    <form-select-user
                        :id="`${report_id}-user-filter`"
                        name="user_id"
                        :value="state.user_id"
                        :editable="true"
                        :required="false"
                        :filter="designers_filter"
                        :extra-users="fake_users"
                        class="uk-width-1-5"
                        style="margin: 0 auto;"
                        @input="filter_by_user"
                    />
                </form>
                <div class="uk-text-right">
                    <div class="uk-button-group lam-graph-filter">
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 6}"
                            @click.prevent="set_state_value('scope', 6)"
                        >
                            6 weeks
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 12}"
                            @click.prevent="set_state_value('scope', 12)"
                        >
                            3 months
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 26}"
                            @click.prevent="set_state_value('scope', 26)"
                        >
                            6 months
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 52}"
                            @click.prevent="set_state_value('scope', 52)"
                        >
                            1 year
                        </base-button>
                    </div>
                </div>
            </div>

            <div class="uk-grid uk-grid-small uk-margin-bottom lam-graph">
                <div class="uk-width-1-1" style="position: relative;">
                    <project-workload-graph
                        ref="chart"
                        :chart-data="chart_data"
                        :annotations="chart_annotations"
                        :y-max="ymax"
                        style="height: 600px;"
                        @click="route_to_record"
                    />
                    <div class="uk-button-group lam-graph-toggles">
                        <base-button
                            flavor="link"
                            size="mini"
                            @click.prevent="datasets_set_visibility(false)"
                        >
                            Hide All
                        </base-button>
                        <base-button
                            flavor="link"
                            size="mini"
                            @click.prevent="datasets_set_visibility(true)"
                        >
                            Show All
                        </base-button>
                    </div>
                </div>
            </div>
        </div>
    </report>
</template>

<script>
import { draw } from "patternomaly"
import numeral from "numeral"

import is_report from "./IsReport"
import handles_projects from "./HandlesProjects"
import uses_color_palette from "../../mixins/UsesColorPalette"

import FormSelectUser from "../../ui/FormSelectUser"
import BaseButton from "../../ui/BaseButton"
import OpenIcon from "../../ui/OpenIcon"
import Report from "./Report.vue"
import ProjectWorkloadGraph from "./ProjectWorkloadGraph.js"

export default {
    mixins: [is_report, handles_projects, uses_color_palette],
    components: {
        FormSelectUser,
        BaseButton,
        OpenIcon,
        Report,
        ProjectWorkloadGraph
    },
    computed: {
        fake_users () {
            return [
                { id: -1, initials: "CB" },
                { id: -2, initials: "CP" }
            ]
        },
        user () {
            if(this.state.user_id) {
                if(this.state.user_id < 0) return this.fake_users.find(user => user.id === this.state.user_id)
                return this.raw_user_records.find(user => user.id === this.state.user_id)
            }
            return null
        },
        chart_data () {
            const data = { labels: [], datasets: [] }
            const time_off_dataset = {
                label: "OFF",
                backgroundColor: draw("diagonal", "#ddd"),
                categoryPercentage: 0.9,
                barPercentage: 0.8,
                data: []
            }

            const range_start = this.$moment().startOf("week")
            const range_end = this.$moment(range_start).add(this.state.scope, "weeks")

            if(this.report) {
                const project_ids = this.filtered_data.map(project => project.id)
                const user = this.user
                const project_datasets = new Map()
                const report_dataset = this.report.dataset
                let dataset = null
                const palette = this.extended_palette
                let palette_index = 0
                Object.keys(report_dataset).forEach(date_string => {
                    const date = this.$moment(date_string)
                    if(date.isSameOrAfter(range_start) && date.isSameOrBefore(range_end)) {
                        data.labels.push(date.format("MMM Do"))

                        const days_off = this.off_days(user, date, this.$moment(date).add(6, "days"))
                        time_off_dataset.data.push((days_off * this.$root.applicationSettings.work_day_hours) || null)

                        const raw_project_ids = Object.keys(report_dataset[date_string]).sort()
                        raw_project_ids.forEach(raw_project_id => {
                            const project_id = parseInt(raw_project_id.replace(/^[^;]+;(\d+)$/, "$1"))
                            if(project_ids.indexOf(project_id) >= 0) {
                                if(!project_datasets.has(raw_project_id)) {
                                    const project = this.entity_record("project", project_id) || {}
                                    dataset = {
                                        // label: this.nibnut_filter("nibnut.smart-truncate", [project.number + (parseInt(project.number_suffix) ? `.${project.number_suffix}`  : "") + " " + project.name, 30]),
                                        // label: this.nibnut_filter("nibnut.smart-truncate", [`${project.number}.${project.number_suffix} ${project.name}`, 30]),
                                        backgroundColor: project.hex_color,
                                        borderColor: "#ffffff",
                                        borderWidth: 1,
                                        categoryPercentage: 0.9,
                                        barPercentage: 0.8,
                                        data: new Array(this.state.scope).fill(0),
                                        project: project,
                                        report_user: user
                                    }
                                    if(project.number) dataset.label = this.nibnut_filter("nibnut.smart-truncate", [`${project.number}.${project.number_suffix} ${project.name}`, 30])
                                    else {
                                        dataset.label = this.nibnut_filter("nibnut.smart-truncate", [project.name, 30])
                                        const color = palette[palette_index % palette.length]
                                        palette_index++
                                        dataset.backgroundColor = color.background
                                    }
                                    project_datasets.set(raw_project_id, dataset)
                                }
                                dataset = project_datasets.get(raw_project_id)
                                dataset.data[data.labels.length - 1] = report_dataset[date_string][raw_project_id]
                                project_datasets.set(raw_project_id, dataset)
                                // project_datasets[project_id].data[data.labels.length - 1] = report_dataset[date_string][project_id]
                                // project_datasets[project_id].data.push(report_dataset[date_string][project_id])
                            }
                        })
                    }
                })
                for(const dataset of project_datasets.values()) {
                    data.datasets.push(dataset)
                }
                // data.datasets = Object.values(project_datasets)
                data.datasets.push(time_off_dataset)

                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.ymax = 0
                for(let loop = 0; loop < data.labels.length; loop++) {
                    let period_total = 0
                    data.datasets.forEach(dataset => {
                        period_total += parseFloat(dataset.data[loop]) || 0
                    })
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.ymax = Math.max(period_total, this.ymax)
                }
            }
            const user = this.user || this.profile
            if(user && !!user.annual_utilisation_rate) {
                const hours_per_week = (user.annual_utilisation_rate / 100) * (5 * this.$root.applicationSettings.work_day_hours)
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.ymax = Math.max(hours_per_week, this.ymax)
            }

            return data
        },
        chart_annotations () {
            let annotations = []
            const user = this.user || this.profile
            if(user && !!user.load_lines && !!user.annual_utilisation_rate) {
                const hours_per_week = (user.annual_utilisation_rate / 100) * (5 * this.$root.applicationSettings.work_day_hours)
                annotations = Object.keys(user.load_lines).map(key => {
                    const value = hours_per_week * (user.load_lines[key] / 100)
                    let color = "#27ae60"
                    if(key === "y") color = "#f2c94c"
                    else if(key === "z") color = "#e44e56"
                    return {
                        type: "line",
                        mode: "horizontal",
                        scaleID: "y-axis-0",
                        value,
                        borderColor: color,
                        borderWidth: 1,
                        label: {
                            backgroundColor: "transparent",
                            fontFamily: "'Helvetica Neue', Helvetica, Arial, 'sans-serif'",
                            fontSize: 10,
                            fontStyle: "bold",
                            fontColor: color,
                            position: "left",
                            xPadding: 3,
                            yPadding: 3,
                            xAdjust: 0,
                            yAdjust: 8,
                            cornerRadius: 0,
                            enabled: true,
                            content: `${numeral(user.load_lines[key]).format("0,0")}%`
                        }
                    }
                })
            }
            /*
            const user = this.user || this.profile
            if(user && !!user.annual_utilisation_rate) {
                const hours_per_week = (user.annual_utilisation_rate / 100) * (5 * this.$root.applicationSettings.work_day_hours)
                const fifty_percent = hours_per_week * 0.5
                const one_hundred_fifty_percent = hours_per_week * 1.5
                annotations.push({
                    type: 'line',
                    mode: 'horizontal',
                    scaleID: 'y-axis-0',
                    value: fifty_percent,
                    borderColor: this.color_on_hold.border,
                    borderWidth: 1,
                    label: {
                        backgroundColor: "transparent",
                        fontFamily: "'Helvetica Neue', Helvetica, Arial, 'sans-serif'",
                        fontSize: 10,
                        fontStyle: "bold",
                        fontColor: this.color_on_hold.border,
                        position: "left",
                        xPadding: 3,
                        yPadding: 3,
                        xAdjust: 0,
                        yAdjust: 8,
                        cornerRadius: 0,
                        enabled: true,
                        content: "50%"
                    }
                })

                annotations.push({
                    type: 'line',
                    mode: 'horizontal',
                    scaleID: 'y-axis-0',
                    value: hours_per_week,
                    borderColor: this.color_on_hold.border,
                    borderWidth: 1,
                    label: {
                        backgroundColor: "transparent",
                        fontFamily: "'Helvetica Neue', Helvetica, Arial, 'sans-serif'",
                        fontSize: 10,
                        fontStyle: "bold",
                        fontColor: this.color_on_hold.border,
                        position: "left",
                        xPadding: 3,
                        yPadding: 3,
                        xAdjust: 0,
                        yAdjust: 8,
                        cornerRadius: 0,
                        enabled: true,
                        content: "100%"
                    }
                })

                annotations.push({
                    type: 'line',
                    mode: 'horizontal',
                    scaleID: 'y-axis-0',
                    value: one_hundred_fifty_percent,
                    borderColor: this.color_on_hold.border,
                    borderWidth: 1,
                    label: {
                        backgroundColor: "transparent",
                        fontFamily: "'Helvetica Neue', Helvetica, Arial, 'sans-serif'",
                        fontSize: 10,
                        fontStyle: "bold",
                        fontColor: this.color_on_hold.border,
                        position: "left",
                        xPadding: 3,
                        yPadding: 3,
                        xAdjust: 0,
                        yAdjust: 8,
                        cornerRadius: 0,
                        enabled: true,
                        content: "150%"
                    }
                })
            }
            */

            return annotations
        }
    },
    data () {
        return {
            report_id: "project_workload",

            default_state: {
                scope: 6, // nb_weeks
                mode: "absolute",
                filter: "",
                user_id: 0,
                studio_id: 0,
                version_id: 0
            }
        }
    }
}
</script>
